/**
 * /payroll/setup — Payroll Setup Guide + AI Interview
 *
 * Two-column layout:
 *   Left (~380px): setup checklist with live status polling
 *   Right (flex-1): AI setup assistant chat interface
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SendIcon from "@mui/icons-material/Send";
import { api } from "@/lib/api";
import { useToast } from "@/components/Toaster";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/setup")({
  component: PayrollSetupPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface ChecklistItem {
  key: string;
  label: string;
  status: "done" | "pending" | "optional";
  detail: string | null;
  action_url: string | null;
}

interface SetupStatusOut {
  items: ChecklistItem[];
  overall: "not_started" | "in_progress" | "ready";
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

// ── Checklist panel ───────────────────────────────────────────────────────────

function StatusBanner({ overall, items }: { overall: SetupStatusOut["overall"]; items: ChecklistItem[] }) {
  if (overall === "ready") {
    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        Ready to run payroll
      </Alert>
    );
  }
  const pending = items.filter((i) => i.status === "pending").length;
  if (overall === "in_progress") {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        In progress — {pending} step{pending !== 1 ? "s" : ""} remaining
      </Alert>
    );
  }
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      Not started — complete the steps below to run your first payroll.
    </Alert>
  );
}

function ItemIcon({ status }: { status: ChecklistItem["status"] }) {
  if (status === "done") return <CheckCircleIcon color="success" fontSize="small" />;
  if (status === "optional") return <RemoveCircleOutlineIcon color="warning" fontSize="small" />;
  return <RadioButtonUncheckedIcon color="disabled" fontSize="small" />;
}

function ChecklistRow({ item }: { item: ChecklistItem }) {
  const navigate = useNavigate();
  const isDone = item.status === "done";

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1.5}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1,
        border: "1px solid",
        borderColor: isDone ? "success.light" : item.status === "optional" ? "divider" : "primary.light",
        bgcolor: isDone ? "success.50" : "background.paper",
        opacity: isDone ? 0.8 : 1,
      }}
    >
      {/* Status icon */}
      <Box flexShrink={0} mt={0.1}>
        <ItemIcon status={item.status} />
      </Box>

      {/* Label + detail */}
      <Box flex={1} minWidth={0}>
        <Typography
          variant="body2"
          fontWeight={isDone ? 400 : 500}
          color={isDone ? "text.secondary" : "text.primary"}
          sx={{ textDecoration: isDone ? "line-through" : "none" }}
        >
          {item.label}
          {item.status === "optional" && (
            <Typography component="span" variant="caption" color="text.disabled" ml={0.5}>
              (optional)
            </Typography>
          )}
        </Typography>
        {item.detail && (
          <Typography variant="caption" color="text.secondary" display="block">
            {item.detail}
          </Typography>
        )}
      </Box>

      {/* Action button — always visible so user can jump directly without the interview */}
      {item.action_url && (
        <Button
          size="small"
          variant={isDone ? "text" : "outlined"}
          color={isDone ? "inherit" : "primary"}
          sx={{ flexShrink: 0, fontSize: 11, px: 1 }}
          onClick={() => navigate({ to: item.action_url as never })}
        >
          {isDone ? "Review" : "Configure →"}
        </Button>
      )}
    </Box>
  );
}

function ChecklistPanel({
  status,
  onFeedbackClick,
}: {
  status: SetupStatusOut | undefined;
  onFeedbackClick: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="h6" fontWeight={600}>
          Payroll Setup
        </Typography>
        <HelpHint text="Complete these steps to run your first payroll. Click any item to configure it directly — no need to use the assistant." />
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Click any step to configure it directly, or use the assistant on the right.
      </Typography>

      {status ? (
        <>
          <StatusBanner overall={status.overall} items={status.items} />
          <Stack spacing={1} flex={1}>
            {status.items.map((item) => (
              <ChecklistRow key={item.key} item={item} />
            ))}
          </Stack>
        </>
      ) : (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Button
        variant="text"
        color="warning"
        size="small"
        onClick={onFeedbackClick}
        sx={{ mt: 3, alignSelf: "flex-start" }}
      >
        I'm stuck — send feedback
      </Button>
    </Paper>
  );
}

// ── Chat panel ────────────────────────────────────────────────────────────────

const INITIAL_MESSAGE: ChatMessage = {
  role: "assistant",
  content:
    "Hi! I'm your payroll setup assistant. I can walk you through configuring payroll for your company. What would you like help with first?",
};

function ChatBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === "user";
  return (
    <Box
      display="flex"
      justifyContent={isUser ? "flex-end" : "flex-start"}
      mb={1}
    >
      <Box
        sx={{
          maxWidth: "75%",
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: isUser ? "primary.main" : "grey.100",
          color: isUser ? "primary.contrastText" : "text.primary",
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {msg.content}
        </Typography>
      </Box>
    </Box>
  );
}

function ChatPanel({ setupItems }: { setupItems: ChecklistItem[] | undefined }) {
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_MESSAGE]);
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  const chatMutation = useMutation({
    mutationFn: async (payload: { message: string; history: ChatMessage[] }) => {
      const res = await api<{ reply: string; role: string }>("/api/payroll/setup/chat", {
        method: "POST",
        body: payload,
      });
      return res;
    },
    onSuccess: (data) => {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply },
      ]);
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong. Please try again." },
      ]);
    },
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || chatMutation.isPending) return;
    const newUserMsg: ChatMessage = { role: "user", content: text };
    const updatedMessages = [...messages, newUserMsg];
    setMessages(updatedMessages);
    setInput("");
    // Send last 20 history entries (excluding the just-added user message)
    const history = updatedMessages.slice(0, -1).slice(-20);
    chatMutation.mutate({ message: text, history });
  };

  return (
    <Paper
      variant="outlined"
      sx={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
    >
      <Box px={3} py={2} borderBottom="1px solid" borderColor="divider">
        <Typography variant="h6" fontWeight={600}>
          Setup Assistant
        </Typography>
      </Box>

      <Box
        flex={1}
        overflow="auto"
        px={2}
        py={2}
        sx={{ minHeight: 0 }}
      >
        {messages.map((msg, idx) => (
          <ChatBubble key={idx} msg={msg} />
        ))}
        {chatMutation.isPending && (
          <Box display="flex" justifyContent="flex-start" mb={1} pl={1}>
            <CircularProgress size={18} />
          </Box>
        )}
        <div ref={bottomRef} />
      </Box>

      <Box
        display="flex"
        gap={1}
        px={2}
        py={2}
        borderTop="1px solid"
        borderColor="divider"
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Ask a setup question…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={chatMutation.isPending}
          multiline
          maxRows={4}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!input.trim() || chatMutation.isPending}
          sx={{ alignSelf: "flex-end" }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
}

// ── Feedback dialog ───────────────────────────────────────────────────────────

function FeedbackDialog({
  open,
  onClose,
  checklistLabels,
}: {
  open: boolean;
  onClose: () => void;
  checklistLabels: string[];
}) {
  const toast = useToast();
  const [stuckAt, setStuckAt] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  const feedbackMutation = useMutation({
    mutationFn: async (body: { stuck_at: string; message: string; contact_email?: string }) => {
      await api("/api/payroll/setup/feedback", { method: "POST", body });
    },
    onSuccess: () => {
      toast.success("Thanks — we've received your feedback.");
      setStuckAt("");
      setMessage("");
      setEmail("");
      onClose();
    },
    onError: () => {
      toast.error("Failed to send feedback. Please try again.");
    },
  });

  const handleSubmit = () => {
    if (!stuckAt || !message) return;
    feedbackMutation.mutate({
      stuck_at: stuckAt,
      message,
      contact_email: email || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Setup Feedback</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            select
            label="Where are you stuck?"
            value={stuckAt}
            onChange={(e) => setStuckAt(e.target.value)}
            fullWidth
            size="small"
          >
            {checklistLabels.map((label) => (
              <MenuItem key={label} value={label}>
                {label}
              </MenuItem>
            ))}
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
          <TextField
            label="What happened / what did you try?"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={4}
          />
          <TextField
            label="Your email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
            type="email"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={feedbackMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!stuckAt || !message || feedbackMutation.isPending}
          startIcon={feedbackMutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          Send Feedback
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Page root ─────────────────────────────────────────────────────────────────

function PayrollSetupPage() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const { data: setupStatus } = useQuery({
    queryKey: ["payroll-setup-status"],
    queryFn: () => api<SetupStatusOut>("/api/payroll/setup-status"),
    refetchInterval: 30_000,
  });

  const checklistLabels = setupStatus?.items.map((i) => i.label) ?? [];

  return (
    <Box
      display="flex"
      gap={3}
      p={3}
      height="calc(100vh - 64px)"
      minHeight={0}
      overflow="hidden"
    >
      {/* Left: checklist */}
      <Box width={380} flexShrink={0} height="100%">
        <ChecklistPanel
          status={setupStatus}
          onFeedbackClick={() => setFeedbackOpen(true)}
        />
      </Box>

      {/* Right: chat */}
      <Box flex={1} minWidth={0} height="100%">
        <ChatPanel setupItems={setupStatus?.items} />
      </Box>

      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        checklistLabels={checklistLabels}
      />
    </Box>
  );
}
